import { asError } from '@/util/errorUtil'
import { Fragment, jsx } from '@amazon/vinyl-tsx'
import { ErrorMessage } from '@/components/ErrorMessage'
import type { Maybe } from '@amazon/vinyl-util'

/**
 * Props for the AsyncComponentLoader.
 */
export interface AsyncComponentLoaderProps<T> {
    /**
     * The async function that will be executed to load data.
     */
    readonly loadData: () => Promise<T>

    /**
     * Function that creates an element using the loaded data.
     */
    readonly createComponent: (data: T) => HTMLElement

    /**
     * Optional custom loading element.
     */
    readonly loadingIndicator?: Maybe<HTMLElement>

    /**
     * Optional function to create an error element.
     */
    readonly createErrorElement?: (props: { readonly message: string }) => HTMLElement

    /**
     * Optional callback for handling errors.
     */
    readonly onError?: (error: Error) => void
}

const defaultCreateErrorElement = ErrorMessage

/**
 * A component that handles async operations before rendering a component with the loaded data.
 */
export function AsyncComponentLoader<T>({
    loadData,
    createComponent,
    loadingIndicator,
    createErrorElement,
    onError,
}: AsyncComponentLoaderProps<T>) {
    const container = <></>

    if (loadingIndicator) {
        container.append(loadingIndicator)
    }

    // Start loading data
    ;(async () => {
        try {
            const result = await loadData()
            // Add the component with loaded data
            container.append(createComponent(result))
        } catch (error: unknown) {
            const thrownError = asError(error)

            // Call error callback if provided
            onError?.(thrownError)

            // Add error element
            container.append((createErrorElement ?? defaultCreateErrorElement)(thrownError))
        } finally {
            if (loadingIndicator) container.removeChild(loadingIndicator)
        }
        // The try/catch above already routes load failures to onError + the
        // error element; this guard only covers a throw from finally itself.
    })().catch((error: unknown) => onError?.(asError(error)))

    return container
}
